export interface AltimetryPoint {
  x: number
  y: number
  altitude: number
}

export interface AltimetryData {
  altimetry_grid: AltimetryPoint[]
  precision: number
  altitude_min: number
  altitude_max: number
}

const COMPASS_SECTORS = [
  'North',
  'North-East',
  'East',
  'South-East',
  'South',
  'South-West',
  'West',
  'North-West',
]

/**
 * Map a downslope vector to one of eight compass sectors — the direction a
 * slope faces. Components are (east, north) and point toward lower ground;
 * the azimuth is measured clockwise from North.
 */
const downslopeToCompass = (east: number, north: number): string => {
  let deg = (Math.atan2(east, north) * 180) / Math.PI
  if (deg < 0) deg += 360
  return COMPASS_SECTORS[Math.round(deg / 45) % 8]
}

/**
 * Calculate slope and aspect from altimetry data
 * @param altimetryData - The altimetry data
 */
export const calculateTerrainMetrics = (altimetryData: AltimetryData) => {
  const { altimetry_grid } = altimetryData

  if (altimetry_grid.length < 4) {
    return {
      averageSlope: 0,
      maxSlope: 0,
      slopeDistribution: {},
      primaryAspect: 'Unknown',
      maxFlatArea: 0,
      flatAreaPercentage: 0,
      reliableSlopeData: false,
      gridSize: { x: 0, y: 0 },
    }
  }

  // Sort points to create a grid
  const uniqueX = [...new Set(altimetry_grid.map((p) => p.x))].sort(
    (a, b) => a - b
  )
  const uniqueY = [...new Set(altimetry_grid.map((p) => p.y))].sort(
    (a, b) => a - b
  )

  // Check if the plot is large enough for reliable slope calculations
  const plotWidth =
    uniqueX.length > 0 ? uniqueX[uniqueX.length - 1] - uniqueX[0] : 0
  const plotHeight =
    uniqueY.length > 0 ? uniqueY[uniqueY.length - 1] - uniqueY[0] : 0

  // We need at least 10m x 10m for reliable slope calculations with our 5m sampling
  const reliableSlopeData = plotWidth >= 10 && plotHeight >= 10

  // Calculate grid spacing (assuming uniform grid)
  const gridSpacingX = uniqueX.length > 1 ? uniqueX[1] - uniqueX[0] : 1
  const gridSpacingY = uniqueY.length > 1 ? uniqueY[1] - uniqueY[0] : 1
  const cellArea = gridSpacingX * gridSpacingY // Area of each grid cell in m²

  // Create a 2D grid
  const gridMap = new Map<string, number>()
  altimetry_grid.forEach((point) => {
    gridMap.set(`${point.x},${point.y}`, point.altitude)
  })

  const slopes: number[] = []
  // Per-point downslope vectors (east/north components, toward lower ground),
  // pushed 1:1 with `slopes` so the two arrays stay index-aligned.
  const aspectVectors: { downEast: number; downNorth: number }[] = []
  const slopeMap = new Map<string, number>() // Store slope for each grid cell

  // Calculate slopes over larger distances for more realistic terrain analysis
  // We'll try to sample points that are approximately 5 meters apart
  // But we need to work with the actual grid points
  const targetSampleDistance = 5 // Target distance in meters

  // Calculate how many grid points to skip to get approximately 5m distance
  const skipX = Math.max(1, Math.round(targetSampleDistance / gridSpacingX))
  const skipY = Math.max(1, Math.round(targetSampleDistance / gridSpacingY))

  // Calculate slopes between points that are further apart
  for (let i = 0; i < uniqueX.length; i++) {
    for (let j = 0; j < uniqueY.length; j++) {
      const x1 = uniqueX[i]
      const y1 = uniqueY[j]
      const z1 = gridMap.get(`${x1},${y1}`)

      if (z1 === undefined) continue

      // Calculate slope using points that are skipX/skipY indices away
      // This gives us more realistic terrain slopes
      let totalSlope = 0
      let slopeCount = 0
      let maxSlope = 0

      // Check in multiple directions using grid indices
      const directions = [
        [skipX, 0], // East
        [-skipX, 0], // West
        [0, skipY], // North
        [0, -skipY], // South
        [skipX, skipY], // NE
        [-skipX, skipY], // NW
        [skipX, -skipY], // SE
        [-skipX, -skipY], // SW
      ]

      directions.forEach(([di, dj]) => {
        const i2 = i + di
        const j2 = j + dj

        // Check bounds
        if (i2 >= 0 && i2 < uniqueX.length && j2 >= 0 && j2 < uniqueY.length) {
          const x2 = uniqueX[i2]
          const y2 = uniqueY[j2]
          const z2 = gridMap.get(`${x2},${y2}`)

          if (z2 !== undefined) {
            // Calculate the actual distance in meters
            const dx = x2 - x1
            const dy = y2 - y1
            const distance = Math.sqrt(dx * dx + dy * dy)
            const dz = z2 - z1

            // Calculate slope angle in degrees
            const slope_rad = Math.atan(Math.abs(dz) / distance)
            const slope_degrees = slope_rad * (180 / Math.PI)

            totalSlope += slope_degrees
            slopeCount++
            maxSlope = Math.max(maxSlope, slope_degrees)
          }
        }
      })

      // Use average slope for this point
      if (slopeCount > 0) {
        const avgSlope = totalSlope / slopeCount
        slopes.push(avgSlope)
        slopeMap.set(`${x1},${y1}`, avgSlope)

        // Local aspect as a *downslope* vector, i.e. pointing toward lower
        // ground — the direction the slope faces. Centered differences where
        // both opposite neighbours exist; a missing pair leaves that axis 0.
        let northZ, southZ, eastZ, westZ

        if (j + skipY < uniqueY.length) {
          northZ = gridMap.get(`${x1},${uniqueY[j + skipY]}`)
        }
        if (j - skipY >= 0) {
          southZ = gridMap.get(`${x1},${uniqueY[j - skipY]}`)
        }
        if (i + skipX < uniqueX.length) {
          eastZ = gridMap.get(`${uniqueX[i + skipX]},${y1}`)
        }
        if (i - skipX >= 0) {
          westZ = gridMap.get(`${uniqueX[i - skipX]},${y1}`)
        }

        // > 0 when the ground drops toward the east / north respectively.
        let downEast = 0,
          downNorth = 0
        if (eastZ !== undefined && westZ !== undefined) {
          downEast = westZ - eastZ
        }
        if (northZ !== undefined && southZ !== undefined) {
          downNorth = southZ - northZ
        }
        aspectVectors.push({ downEast, downNorth })
      }
    }
  }

  // Find maximum contiguous flat area (slope < 5°)
  const flatThreshold = 5 // degrees
  const visited = new Set<string>()
  const regions: number[] = [] // Array to store area of each flat region

  // Helper function for flood fill to find contiguous flat regions
  const floodFill = (startX: number, startY: number): number => {
    const stack = [[startX, startY]]
    let regionArea = 0

    while (stack.length > 0) {
      const [x, y] = stack.pop()!
      const key = `${x},${y}`

      if (visited.has(key)) continue

      const slope = slopeMap.get(key)
      if (slope === undefined || slope > flatThreshold) continue

      visited.add(key)
      regionArea += cellArea

      // Check all 4 adjacent cells
      const xIndex = uniqueX.indexOf(x)
      const yIndex = uniqueY.indexOf(y)

      if (xIndex > 0) stack.push([uniqueX[xIndex - 1], y])
      if (xIndex < uniqueX.length - 1) stack.push([uniqueX[xIndex + 1], y])
      if (yIndex > 0) stack.push([x, uniqueY[yIndex - 1]])
      if (yIndex < uniqueY.length - 1) stack.push([x, uniqueY[yIndex + 1]])
    }

    return regionArea
  }

  // Find all flat regions
  uniqueX.forEach((x) => {
    uniqueY.forEach((y) => {
      const key = `${x},${y}`
      if (!visited.has(key) && slopeMap.has(key)) {
        const regionArea = floodFill(x, y)
        if (regionArea > 0) {
          regions.push(regionArea)
        }
      }
    })
  })

  const maxFlatArea = regions.length > 0 ? Math.max(...regions) : 0
  const totalArea = altimetry_grid.length * cellArea
  const flatAreaPercentage = totalArea > 0 ? (maxFlatArea / totalArea) * 100 : 0

  // Calculate statistics
  const averageSlope =
    slopes.length > 0 ? slopes.reduce((a, b) => a + b, 0) / slopes.length : 0

  const maxSlope = slopes.length > 0 ? Math.max(...slopes) : 0

  // Calculate slope distribution in degrees
  const slopeDistribution = {
    '0-5°': slopes.filter((s) => s <= 5).length,
    '5-10°': slopes.filter((s) => s > 5 && s <= 10).length,
    '10-15°': slopes.filter((s) => s > 10 && s <= 15).length,
    '15-25°': slopes.filter((s) => s > 15 && s <= 25).length,
    '>25°': slopes.filter((s) => s > 25).length,
  }

  // Calculate aspect distribution
  const aspectDistribution = {
    North: 0,
    'North-East': 0,
    East: 0,
    'South-East': 0,
    South: 0,
    'South-West': 0,
    West: 0,
    'North-West': 0,
    Flat: 0,
  }

  // Categorize each point's downslope direction into a compass sector, but
  // only where there's meaningful slope (>2°) and a non-zero aspect vector.
  aspectVectors.forEach(({ downEast, downNorth }, index) => {
    const magnitude = Math.hypot(downEast, downNorth)
    if (slopes[index] > 2 && magnitude > 0) {
      const sector = downslopeToCompass(
        downEast,
        downNorth
      ) as keyof typeof aspectDistribution
      aspectDistribution[sector]++
    } else {
      aspectDistribution['Flat']++
    }
  })

  // Determine the dominant orientation as the slope-weighted circular mean of
  // the per-point downslope vectors: summing the vectors weights steeper cells
  // more (their vectors are longer) and, unlike averaging raw angles, is immune
  // to the ±180° wrap-around. The resultant points toward lower ground — the
  // direction the parcel faces.
  let sumDownEast = 0,
    sumDownNorth = 0
  aspectVectors.forEach(({ downEast, downNorth }) => {
    sumDownEast += downEast
    sumDownNorth += downNorth
  })
  const resultantMagnitude = Math.hypot(sumDownEast, sumDownNorth)

  let primaryAspect = 'Flat'
  if (averageSlope > 2 && resultantMagnitude > 0) {
    primaryAspect = downslopeToCompass(sumDownEast, sumDownNorth)
  }

  // Find the largest flat region for visualization
  let largestRegion: Set<string> = new Set()

  // Reset visited for finding the largest region
  visited.clear()

  // Modified flood fill to track the actual region
  const floodFillTracked = (startX: number, startY: number): Set<string> => {
    const stack = [[startX, startY]]
    const region = new Set<string>()

    while (stack.length > 0) {
      const [x, y] = stack.pop()!
      const key = `${x},${y}`

      if (visited.has(key)) continue

      const slope = slopeMap.get(key)
      if (slope === undefined || slope > flatThreshold) continue

      visited.add(key)
      region.add(key)

      // Check all 4 adjacent cells
      const xIndex = uniqueX.indexOf(x)
      const yIndex = uniqueY.indexOf(y)

      if (xIndex > 0) stack.push([uniqueX[xIndex - 1], y])
      if (xIndex < uniqueX.length - 1) stack.push([uniqueX[xIndex + 1], y])
      if (yIndex > 0) stack.push([x, uniqueY[yIndex - 1]])
      if (yIndex < uniqueY.length - 1) stack.push([x, uniqueY[yIndex + 1]])
    }

    return region
  }

  // Find the largest flat region
  uniqueX.forEach((x) => {
    uniqueY.forEach((y) => {
      const key = `${x},${y}`
      if (!visited.has(key) && slopeMap.has(key)) {
        const region = floodFillTracked(x, y)
        if (region.size > largestRegion.size) {
          largestRegion = region
        }
      }
    })
  })

  return {
    averageSlope,
    maxSlope,
    slopeDistribution,
    primaryAspect,
    aspectDistribution,
    totalPoints: altimetry_grid.length,
    maxFlatArea: Math.round(maxFlatArea),
    flatAreaPercentage: flatAreaPercentage.toFixed(1),
    flatRegion: Array.from(largestRegion), // Convert Set to Array for easier use
    reliableSlopeData,
    gridSize: { x: uniqueX.length, y: uniqueY.length },
  }
}

/**
 * Transform altimetry data for ECharts 3D surface plot
 */
export const transformForECharts3D = (altimetryData: AltimetryData) => {
  const { altimetry_grid, altitude_min, altitude_max } = altimetryData

  // Get unique X and Y values
  const xValues = [...new Set(altimetry_grid.map((p) => p.x))].sort(
    (a, b) => a - b
  )
  const yValues = [...new Set(altimetry_grid.map((p) => p.y))].sort(
    (a, b) => a - b
  )

  // Create a 2D array for the surface data
  const surfaceData: number[][] = []

  // Create a map for quick lookup
  const pointMap = new Map<string, number>()
  altimetry_grid.forEach((point) => {
    pointMap.set(`${point.x},${point.y}`, point.altitude)
  })

  // Build the surface data - only use actual data points, no interpolation
  yValues.forEach((y) => {
    const row: number[] = []
    xValues.forEach((x) => {
      const altitude = pointMap.get(`${x},${y}`)
      // Use NaN for missing points so ECharts won't render them
      row.push(altitude !== undefined ? altitude : NaN)
    })
    surfaceData.push(row)
  })

  return {
    xAxis: xValues,
    yAxis: yValues,
    data: surfaceData,
    min: altitude_min,
    max: altitude_max,
    actualPoints: altimetry_grid, // Return actual points for scatter plot fallback
  }
}
